import { Text } from "@ui-kitten/components";
import FullScreenSpinner from "components/FullScreenSpinner";
import NewMemberForm from "components/NewMemberForm";
import { login } from "features/slices/authSlice";
import { initialPrimaryUser } from "features/slices/buyFlowSlice";
import { PrimaryUser } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useTenantUserFields from "hooks/useTenantUserFields";
import i18n from "plugins/localization";
import React, { useState } from "react";
import { View } from "react-native";
import { setUserToken } from "screens/Register/registerUtils";
import accountServices from "services/account.services";
import userServices from "services/user.services";
import toast from "utils/toast";
import { convertUserToUserFields } from "utils/userUtils";
import AgreementText from "./AgreementText";
import Button from "./Button";

interface Props {
  title: string;
  marketplace?: boolean;
  onLoginClick: () => void;
}
const Signup = (props: Props) => {
  const { title, marketplace, onLoginClick } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  // Prefill from the same slice LoginForm reads, so a flow that captured
  // the shopper's email (e.g. the Shop V3 email gate) seeds signup too.
  const storedEmail = useAppSelector((state) => state.loginFlow.email);
  const { tenantUserFields } = useTenantUserFields();
  const createAssociateAccount = async (values: PrimaryUser) => {
    try {
      setLoading(true);
      // see if email exists
      const emailUsed = await userServices.userExists(values.email!);
      if (emailUsed) {
        toast.error("This email address is already in use, please log in");
        return;
      }
      // register user
      const registerReponse = (await userServices.register(
        values,
        convertUserToUserFields({
          user: values,
          tenantOrPlanPackageUserFields: tenantUserFields!,
        }),
      )) as any;
      // set token in async storage
      await setUserToken(values);
      // create associate account
      await accountServices.createAccount({
        type: "ASSOCIATE",
        primaryUser: {
          userId: registerReponse.id,
          planPackageIds: [],
        },
        marketplace,
        source: "FANRALLY_APP",
        smsMarketingConsent: false,
        emailMarketingConsent: false,
      });
      // login
      dispatch(login({ username: values.email!, password: values.password! }));
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <FullScreenSpinner
        style={{ padding: 8 }}
        text={i18n.t("screens.Register.creatingAccountMessage")}
      />
    );

  return (
    <View>
      <Text category="h6" style={{ textAlign: "center", marginBottom: 4 }}>
        {title}
      </Text>
      <Button
        size="small"
        appearance="ghost"
        onPress={onLoginClick}
        status="info"
      >
        <Text>Already have an account? Sign in here</Text>
      </Button>

      <NewMemberForm
        TOSRequired={marketplace}
        initialValues={{ ...initialPrimaryUser, email: storedEmail || "" }}
        onSubmit={createAssociateAccount}
        showImageUploader={false}
        btnText={i18n.t("common.createAccountBtnText")}
        includePassword
        creatingAssociateAccount
      />
      <View className="mt-4">
        <AgreementText
          variant="TEXT"
          btnText={i18n.t("common.createAccountBtnText")}
        />
      </View>
    </View>
  );
};

export default Signup;
